import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";
import Sidebar from "./Sidebar";
import BrandLogo from "./BrandLogo";
import AdOverlay from "./AdOverlay";
import MobileNavbar from "./MobileNavbar";
import CartIcon from "./CartIcon";
import AdminNotifications from "./AdminNotifications";

/**
 * Layout principal del sitio: barra lateral fija como navegación principal
 * en escritorio y barra de navegación inferior adaptativa en móvil/tablet.
 */
export default function SiteLayout() {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Desplazamiento suave a las anclas (por ejemplo, /#como-funciona).
  // El contenido de la página (productos, carruseles) carga de forma
  // asíncrona y cambia de tamaño, por lo que se reintenta el salto hasta
  // que la posición de la sección se estabiliza. Así nunca queda "a medias".
  useEffect(() => {
    if (!location.hash) return;

    const hash = location.hash;
    let cancelled = false;
    let timer = 0;
    let attempts = 0;
    let stableCount = 0;
    let lastTop = -1;

    const step = () => {
      if (cancelled) return;
      const target = document.querySelector(hash) as HTMLElement | null;

      if (target) {
        const top = Math.round(
          target.getBoundingClientRect().top + window.scrollY,
        );

        if (top === lastTop) {
          stableCount += 1;
        } else {
          stableCount = 0;
          lastTop = top;
        }

        target.scrollIntoView({ behavior: "smooth", block: "start" });

        // Se detiene cuando la sección lleva dos comprobaciones sin moverse.
        if (stableCount >= 2) return;
      }

      if (attempts >= 14) return;
      attempts += 1;
      timer = window.setTimeout(step, 150);
    };

    // Pequeño respiro para que el primer render de la página se complete.
    timer = window.setTimeout(step, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen w-full bg-background-50">
      {/* Barra lateral fija (escritorio) */}
      <div className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block">
        <Sidebar />
      </div>

      {/* Barra superior (móvil/tablet) */}
      <header className="glass-strong sticky top-0 z-30 flex h-14 items-center gap-3 px-4 lg:hidden">
        <BrandLogo />

        <div className="ml-auto flex items-center gap-1">
          <CartIcon />
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Cambiar tema"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className={`text-xl ${theme === "dark" ? "ri-sun-line" : "ri-moon-line"}`}></i>
          </button>
        </div>
      </header>

      {/* Contenido de la página */}
      <main className="pb-24 lg:pl-64 lg:pb-0">
        <Outlet />
      </main>

      {/* Barra de navegación inferior adaptativa (solo móvil) */}
      <MobileNavbar />

      {/* Alertas para administradores (solicitudes de vendedor, etc.) */}
      <AdminNotifications />

      {/* Anuncio promocional a pantalla completa (solo móvil) */}
      <AdOverlay />
    </div>
  );
}